import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, take } from 'rxjs/operators';

import { select, Store } from '@ngrx/store';
import { VehicleInsuranceService, VehicleService } from '@services';
import { selectRouteState } from '@state/route';
import * as VehicleActions from './vehicle.actions';

@Injectable()
export class VehicleEffects {
  constructor(
    private store: Store,
    private actions$: Actions,
    private vehicleService: VehicleService,
    private vehicleInsuranceService: VehicleInsuranceService
  ) {}

  fetchVehicles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleActions.fetchVehicles),
      switchMap(() =>
        from(this.vehicleService.get()).pipe(
          map((vehicles) => VehicleActions.fetchVehiclesSuccess({ vehicles })),
          catchError((error) =>
            of(VehicleActions.fetchVehiclesFailed({ error }))
          )
        )
      )
    )
  );

  fetchVehicleById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleActions.fetchVehicleById),
      switchMap(({ vehicleId }) =>
        from(this.vehicleService.getById(vehicleId)).pipe(
          map((vehicle) => VehicleActions.fetchVehicleByIdSuccess({ vehicle })),
          catchError((error) =>
            of(VehicleActions.fetchVehicleByIdFailed({ error }))
          )
        )
      )
    )
  );

  fetchCurrentVehicle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleActions.fetchCurrentVehicle),
      mergeMap(() => this.store.pipe(select(selectRouteState), take(1))),
      switchMap((route) => {
        const vehicleId = route.params['vehicleId'];
        return of(VehicleActions.fetchVehicleById({ vehicleId }));
      })
    )
  );

  fetchCurrentVehicleInsurance$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleActions.fetchCurrentVehicleInsurance),
      mergeMap(() => this.store.pipe(select(selectRouteState), take(1))),
      switchMap((route) => {
        const vehicleId = route.params['vehicleId'];
        return from(
          this.vehicleInsuranceService.getByVehicleId(vehicleId)
        ).pipe(
          map((vehicleInsurance) =>
            VehicleActions.fetchCurrentVehicleInsuranceSuccess({
              vehicleInsurance,
              vehicleId,
            })
          ),
          catchError((error) =>
            of(
              VehicleActions.fetchCurrentVehicleInsuranceFailed({
                error,
                vehicleId,
              })
            )
          )
        );
      })
    )
  );

  assignUserToVehicle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleActions.assignUserToVehicle),
      switchMap(({ vehicleId, userId }) =>
        from(this.vehicleService.assignUserToVehicle(vehicleId, userId)).pipe(
          map((vehicle) =>
            VehicleActions.assignUserToVehicleSuccess({ vehicle })
          ),
          catchError((error) =>
            of(VehicleActions.assignUserToVehicleFailed({ error }))
          )
        )
      )
    )
  );

  removeUserFromVehicle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleActions.removeUserFromVehicle),
      switchMap(({ vehicleId, userId }) =>
        from(this.vehicleService.removeUserFromVehicle(vehicleId, userId)).pipe(
          map((vehicle) =>
            VehicleActions.removeUserFromVehicleSuccess({ vehicle })
          ),
          catchError((error) =>
            of(VehicleActions.removeUserFromVehicleFailed({ error }))
          )
        )
      )
    )
  );

  fetchVehicleEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleActions.fetchVehicleEvents),
      switchMap(({ carTrackDriverId, dateRange }) =>
        from(
          this.vehicleService.getVehicleEvents(carTrackDriverId, dateRange)
        ).pipe(
          map((vehicleEvents) =>
            VehicleActions.fetchVehicleEventsSuccess({ vehicleEvents })
          ),
          catchError((error) =>
            of(VehicleActions.fetchVehicleEventsFailed({ error }))
          )
        )
      )
    )
  );

  fetchBatches$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleActions.fetchVehicleBatches),
      switchMap(() =>
        from(this.vehicleService.getBatches()).pipe(
          map((batches) =>
            VehicleActions.fetchVehicleBatchesSuccess({ batches })
          ),
          catchError((error) =>
            of(VehicleActions.fetchVehicleBatchesFailed({ error }))
          )
        )
      )
    )
  );

  fetchBatchById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleActions.fetchVehicleBatchById),
      switchMap(({ batchId }) =>
        from(this.vehicleService.getBatchById(batchId)).pipe(
          map((batch) =>
            VehicleActions.fetchVehicleBatchByIdSuccess({ batch })
          ),
          catchError((error) =>
            of(VehicleActions.fetchVehicleBatchByIdFailed({ error }))
          )
        )
      )
    )
  );

  createBatch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleActions.createVehicleBatch),
      switchMap(({ file }) =>
        from(this.vehicleService.uploadBatch(file)).pipe(
          map((batch) => VehicleActions.createVehicleBatchSuccess({ batch })),
          catchError((error) =>
            of(VehicleActions.createVehicleBatchFailed({ error }))
          )
        )
      )
    )
  );

  processBatch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleActions.processVehicleBatch),
      switchMap(({ batchId }) =>
        from(this.vehicleService.processBatch(batchId)).pipe(
          map(() => VehicleActions.processVehicleBatchSuccess()),
          catchError((error) =>
            of(VehicleActions.processVehicleBatchFailed({ error }))
          )
        )
      )
    )
  );
}
